<template>
  <div
    ref="tabContainerEl"
    :class="['bc-tab-container', type, basicType]"
    :style="containerStyle"
  >
    <div
      v-for="(tab, idx) in tabList"
      :key="tab.label"
      :class="{ focus: props.selectedTab === tab.value }"
      class="bc-tab-wrap"
      @click="scrollToCenter(idx, tab?.disabled)"
    >
      <div
        :class="[
          'bc-tab',
          type,
          {
            active: props.selectedTab === tab.value,
            focus: props.selectedTab === tab.value,
            disabled: tab.disabled,
          },
          parseGAEventName(tab.value),
        ]"
        @click="tabClick(tab, tab?.disabled)"
        :style="tabStyle"
      >
        <slot
          name="prefix"
          :tab="tab"
        >
          <bc-icon
            v-if="tab?.leftIcon"
            :icon="tab.leftIcon"
            :size="tabIconSize"
            :class="['image-icon', props.selectedTab === tab.value ? 'selected' : '']"
            :color="color"
            svg
          />
          <bc-asset-coin
            v-if="tab?.leftImgUrl"
            :image-src="tab.leftImgUrl"
            :width="tabIconSize"
          />
        </slot>
        <div
          v-if="tab.label"
          class="name-wrapper flex gap-1"
        >
          <span>{{ tab.label }}</span>
          <span
            v-if="tab.count != null || tab.count != undefined"
            class="text-theme"
          >{{ tab.count }}</span>
        </div>
        <slot
          name="suffix"
          :tab="tab"
        >
          <bc-icon
            v-if="tab?.rightIcon"
            :icon="tab.rightIcon"
            :size="tabIconSize"
            :class="['image-icon', props.selectedTab === tab.value ? 'selected' : '']"
            :color="color"
            svg
          />
          <bc-asset-coin
            v-if="tab?.rightImgUrl"
            :image-src="tab.rightImgUrl"
            :width="tabIconSize"
          />
        </slot>
      </div>
      <div :class="['bc-tab-underline', type]" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeMount, ref } from 'vue'

import type { COLOR } from '../../types/xangle-vue-ui.types'
import { getGAEventName } from '../../utils/ga.util'
import { parseDomSizeValue } from '../../utils/string.utils'
import BcAssetCoin from '../asset/BcAssetCoin.vue'
import BcIcon from '../icon/BcIcon.vue'
import type { ITab } from './tab.type'

export type IProps<T = any> = {
  tabList: ITab<T>[]
  selectedTab: T
  type?: 'BASIC' | 'NAVI' | 'GROUP'
  tabWidth?: string | number
  height?: string | number
  basicType?: 'GLASS' | 'SOLID' | 'FROSTED'
  color?: COLOR
  gaEventName?: string
}

const props = withDefaults(defineProps<IProps>(), {
  type: 'BASIC',
  color: 'theme',
  height: 40,
})

const emits = defineEmits([ 'update:selectedTab', 'click' ])
const tabContainerEl = ref<HTMLDivElement | null>(null)
const tabIconSize = '24px'

const scrollToCenter = (index: number, disabled?: boolean) => {
  if (disabled) return
  if (!tabContainerEl.value) return
  const item = tabContainerEl.value.children[index] as HTMLDivElement
  const itemWidth = item.offsetWidth
  const containerWidth = tabContainerEl.value.offsetWidth || 0
  tabContainerEl.value.scrollTo({
    behavior: 'smooth',
    left: item.offsetLeft - (containerWidth - itemWidth) / 2,
  })
}

const containerStyle = computed(() => {
  return {
    height: props.height ? parseDomSizeValue(props.height) : 'initial',
  }
})

const tabStyle = computed(() => {
  return {
    width: props.tabWidth ? parseDomSizeValue(props.tabWidth) : 'initial',
  }
})

const hoverBackgroundColor = computed(() => {
  if (props.type === 'BASIC') {
    if (props.basicType === 'GLASS' || props.basicType === 'FROSTED')
      return getGlassColor('o01')
  }
  return 'rgba(var(--bc-dark), var(--o01))'
})

const hoverColor = computed(() => {
  if (props.type === 'BASIC') {
    if (props.basicType === 'GLASS' || props.basicType === 'FROSTED')
      return getGlassColor('o04')
  }
  return 'rgba(var(--bc-dark), var(--o04))'
})

const focusBodyColor = computed(() => {
  if (props.type === 'BASIC') {
    if (props.basicType === 'GLASS') {
      return 'initial'
    }
    if (props.basicType === 'FROSTED')
      return getGlassColor('o01')
    return 'rgba(var(--bc-theme), var(--o1))'
  } else if (props.type === 'NAVI') {
    return undefined
  } else {
    return 'rgba(var(--bc-black), var(--o1))'
  }
})

const focusFontColor = computed(() => {
  if (props.type === 'BASIC') {
    if (props.basicType === 'GLASS' || props.basicType === 'FROSTED') {
      return getGlassColor('o1')
    }
    return 'rgba(var(--bc-absoluteWhite), var(--o1))'
  } else {
    return 'rgba(var(--bc-dark), var(--o1))'
  }
})
const activeColor = computed(() => {
  if (props.type === 'BASIC') {
    if (props.basicType === 'GLASS' || props.basicType === 'FROSTED') return getGlassColor('o1')
    if (props.basicType === 'SOLID') return 'var(--bc-absoluteWhite)'
  }

  return 'rgb(var(--bc-dark))'
})

const activeBackgroundColor = computed(() => {
  if (props.type === 'BASIC') {
    if (props.basicType === 'GLASS' || props.basicType === 'FROSTED')
      return getGlassColor('o01')
  }
  return 'rgba(var(--bc-dark), var(--o01))'
})

const getGlassColor = (opacity: string) => {
  return `rgba(var(--bc-${props.color}), var(--${opacity}))`
}

const parseGAEventName = (value: IProps['selectedTab'])=>{
  if (props.gaEventName) return getGAEventName(`${props.gaEventName}-${value}`)
  return getGAEventName(value)
}

const tabClick = (selectTab: ITab<any>, disabled?: boolean) => {
  if (disabled) return
  emits('update:selectedTab', selectTab.value)
  emits('click', selectTab)
}

onBeforeMount(() => {
  if (props.selectedTab === '' && props.tabList.length > 0) {
    emits('update:selectedTab', props.tabList[0].value)
    emits('click', props.tabList[0])
  }
})
</script>

<style lang="scss" scoped>
.bc-tab-container {
  display: flex;
  align-items: center;
  width: auto;
  margin: 0;
  box-sizing: border-box;
  font-size: 14px;
  font-weight: 700;
  overflow-x: auto;
  gap: 4px;

  &::-webkit-scrollbar {
    display: none;
  }

  &.GROUP {
    padding: 4px;
    border-radius: 8px;
    background-color: rgba(var(--bc-dark), var(--o005));
  }

  &.NAVI {
    gap: initial;
  }

  .bc-tab-wrap {
    height: 100%;
    position: relative;

    .name-wrapper {
      padding: 2px 4px;
    }

    .bc-tab {
      height: 100%;
      box-sizing: border-box;
      border-radius: 8px;
      white-space: nowrap;
      color: rgba(var(--bc-dark), var(--o04));
      transition: 0.3s;
      padding: 8px;
      gap: 8px;
      display: flex;
      justify-content: center;
      align-items: center;
      .image-icon {
        transition: 0.3s;
        opacity: 0.3;
      }
      &.GROUP {
        padding: 4px;
      }

      &:hover {
        color: v-bind(hoverColor);
        background-color: v-bind(hoverBackgroundColor);
        cursor: pointer;
      }

      &:active {
        color: v-bind(activeColor);
        background-color: v-bind(activeBackgroundColor);
        .image-icon {
          opacity: 1;
        }
      }

      &.focus {
        color: v-bind(focusFontColor);
        background-color: v-bind(focusBodyColor);
        .image-icon {
          opacity: 1;
        }
        &:hover {
          cursor: default;
        }
      }

      &.disabled {
        color: rgba(var(--bc-dark), var(--o01));
        &:hover {
          background-color: initial;
          cursor: default;
        }
      }
    }
  }

  .bc-tab-wrap.focus {
    .bc-tab-underline {
      background-color: rgba(var(--bc-theme), var(--o1));
      border-top-left-radius: 4px;
      border-top-right-radius: 4px;
      position: absolute;
      width: 100%;
      bottom: 0;

      &.NAVI {
        height: 4px;
      }
    }
  }
}
</style>